import type { TileKind, ZoneFixture, ZonePayload } from '../../net/protocol';

import { BODY_CLEARANCE, canStep, halfExtent, isSolid, tileAt } from './layout';

/**
 * Doors and breakables, the client's half (2026-10-01). The contract is
 * `doc/grind/design/LEVEL-FIXTURES.md`; **every function here mirrors one in
 * `internal/game/zone/fixtures.go`, line for line**, because prediction and the chase have to refuse
 * exactly what the server refuses. `fixtures.test.ts` and `fixtures_test.go` hold the same cases.
 *
 * `down` is the set of fixture ids that no longer stand (the snapshot's `down`, kept by the store).
 * Null is "everything stands".
 */

/** A barrel's or urn's collision radius. Must equal `BreakableRadius` in Go. */
export const BREAKABLE_RADIUS = 0.45;
/** How close to a closed door a player's centre must come to open it. Must equal `DoorOpenReach`. */
export const DOOR_OPEN_REACH = BODY_CLEARANCE + 0.5;
/** How finely `clearShot` samples a line. Must equal `shotStep`. */
const SHOT_STEP = 0.5;

type Point = { x: number; z: number };
type Down = ReadonlySet<number> | null | undefined;

/** Whether an attack may not pass over a tile: the map's edge and anything solid. `TileKind.BlocksShots`. */
export function blocksShots(kind: TileKind): boolean {
  return kind === 'VOID' || isSolid(kind);
}

const standing = (down: Down, id: number) => !down || !down.has(id);

/** A door's segment. */
export function doorEnds(zone: ZonePayload, f: ZoneFixture): [Point, Point] {
  const h = zone.tileSize / 2;
  return f.alongX
    ? [{ x: f.at.x - h, z: f.at.z }, { x: f.at.x + h, z: f.at.z }]
    : [{ x: f.at.x, z: f.at.z - h }, { x: f.at.x, z: f.at.z + h }];
}

/** How far `p` is from a door's segment, or a breakable's centre. `Layout.DoorDistance`. */
export function fixtureDistance(zone: ZonePayload, f: ZoneFixture, p: Point): number {
  if (f.kind !== 'DOOR') return Math.hypot(p.x - f.at.x, p.z - f.at.z);
  const [a, b] = doorEnds(zone, f);
  return pointSegment(p, a, b);
}

/** How far `p` is inside the body margin of a standing fixture. `Layout.FixtureIntrusion`. */
export function fixtureIntrusion(zone: ZonePayload, p: Point, down: Down): number {
  let worst = 0;
  for (const f of zone.fixtures ?? []) {
    if (!standing(down, f.id)) continue;
    let into: number;
    if (f.kind === 'DOOR') {
      if (Math.abs(p.x - f.at.x) > zone.tileSize || Math.abs(p.z - f.at.z) > zone.tileSize) continue;
      const [a, b] = doorEnds(zone, f);
      into = BODY_CLEARANCE - pointSegment(p, a, b);
    } else {
      into = BREAKABLE_RADIUS + BODY_CLEARANCE - Math.hypot(p.x - f.at.x, p.z - f.at.z);
    }
    worst = Math.max(worst, into);
  }
  return worst;
}

/** Whether the line a→b passes through a standing door. `Layout.CrossesClosedDoor`. */
export function crossesClosedDoor(zone: ZonePayload, a: Point, b: Point, down: Down): boolean {
  for (const f of zone.fixtures ?? []) {
    if (f.kind !== 'DOOR' || !standing(down, f.id)) continue;
    const [p, q] = doorEnds(zone, f);
    if (segmentsCross(a, b, p, q)) return true;
  }
  return false;
}

/**
 * Whether an attack from `a` to `b` is unobstructed: no wall, structure or obstacle on the line and
 * no closed door across it. Pools and pits do not block. `Layout.ClearShot`.
 */
export function clearShot(zone: ZonePayload, a: Point, b: Point, down: Down): boolean {
  const half = halfExtent(zone);
  const d = Math.hypot(b.x - a.x, b.z - a.z);
  const n = Math.ceil(d / SHOT_STEP);
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const x = a.x + (b.x - a.x) * t;
    const z = a.z + (b.z - a.z) * t;
    const col = Math.floor((x + half.x) / zone.tileSize);
    const row = Math.floor((z + half.z) / zone.tileSize);
    if (blocksShots(tileAt(zone, col, row).kind)) return false;
  }
  return !crossesClosedDoor(zone, a, b, down);
}

/**
 * The local player's step rule with the fixtures in it: `canStep`, then no deeper into a standing
 * fixture's margin than it started, and never through a closed door. The rule `walkableAt` applies.
 *
 * **A door the player is leaning on counts as open**, because on the server it is: `openDoorsNear`
 * runs at the start of the player's tick, before the step. Predicting it shut would stop the player
 * at every doorway until the snapshot saying it opened came back.
 */
export function canStepAmong(zone: ZonePayload, from: Point, to: Point, down: Down): boolean {
  if (!canStep(zone, from, to)) return false;
  const fixtures = zone.fixtures ?? [];
  if (fixtures.length === 0) return true;
  let effective = down;
  for (const f of fixtures) {
    if (f.kind === 'DOOR' && standing(effective, f.id) && fixtureDistance(zone, f, from) <= DOOR_OPEN_REACH) {
      effective = new Set([...(effective ?? []), f.id]);
    }
  }
  if (crossesClosedDoor(zone, from, to, effective)) return false;
  return fixtureIntrusion(zone, to, effective) <= Math.max(0, fixtureIntrusion(zone, from, effective)) + 1e-9;
}

function pointSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length2 = dx * dx + dz * dz;
  let t = 0;
  if (length2 > 0) t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / length2));
  return Math.hypot(p.x - (a.x + dx * t), p.z - (a.z + dz * t));
}

function segmentsCross(a: Point, b: Point, p: Point, q: Point): boolean {
  const cross = (o: Point, u: Point, v: Point) => (u.x - o.x) * (v.z - o.z) - (u.z - o.z) * (v.x - o.x);
  const d1 = cross(p, q, a);
  const d2 = cross(p, q, b);
  const d3 = cross(a, b, p);
  const d4 = cross(a, b, q);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0) || d1 === 0 || d2 === 0) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0) || d3 === 0 || d4 === 0) &&
    !(d1 === 0 && d2 === 0)
  );
}
